import { useCallback, useEffect, useMemo } from 'react';
import { LaunchBulkUnwasteTickets } from 'src/components/Listings/InventoryActionDropdown/components/BulkUnwasteTicketsAction/LaunchBulkUnwasteTickets';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkUnwasteTicketsDialog } from 'src/dialogs/BulkEdits/BulkUnwasteTicketsDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  Event,
  ListingQuery,
} from 'src/WebApiController';

import { INVENTORY_BULK_UNWASTE_TICKETS_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';

export const useBulkUnwasteTicketsAction = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const updateKey = event
    ? `unwasteTickets-event-${event.virtId}`
    : INVENTORY_BULK_UNWASTE_TICKETS_KEY;
  const {
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkUnwasteTicketsDialog,
    closeDialog: closeBulkUnwasteTicketsDialog,
  } = useDialog(DialogId.BulkUnwasteTickets, BulkUnwasteTicketsDialog);

  const onSubmit = useCallback(
    async (
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUnwasteTicketsPreview(filterQueryWithEventIds);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUnwasteTicketsPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUnwasteTickets(
              preview!.preview,
              updateKey,
              supportBackgroundProcess
            );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeBulkUnwasteTicketsDialog();
            }
          },
          (error) => {
            showErrorDialog('onSubmitUnwasteTickets', error, {
              trackErrorData: { preview },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      activeAccountWebClientConfig,
      filterQueryWithEventIds,
      showErrorDialog,
      updateKey,
      closeBulkUnwasteTicketsDialog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  const onLaunchAction = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }
      openBulkUnwasteTicketsDialog({
        updateKey,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkUnwasteTicketsDialog,
      });
    },
    [
      closeBulkUnwasteTicketsDialog,
      event,
      onSubmit,
      openBulkUnwasteTicketsDialog,
      setIsLoading,
      setListItemExpansion,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    if (event) {
      setListItemExpansion(true, [event.virtId]);
    }
    openBulkUnwasteTicketsDialog({
      updateKey,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkUnwasteTicketsDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditUnwasteTickets,
        onSelect: openDialogStableRef,
      },
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditUnwasteTickets,
      });
    };
  }, [openDialogStableRef, registerCommand, unregisterCommand]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    return [
      {
        title: ContentId.UnwasteTickets,
        disabled: isLoading,
        onClick: onLaunchAction,
      },
    ];
  }, [isLoading, onLaunchAction]);

  return {
    dropDown: (
      <LaunchBulkUnwasteTickets
        key="UnwasteTickets"
        onClick={onLaunchAction}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
